'use strict';

const { ACTIVE_CLASS, SELECTED_CLASS } = require('../constants');
const { queryFirst, addClass, hasClass } = require('../domUtil');

/**
 * Resets Swiper instance in modal dialog to the first slide
 * Used when tabs or color swatches change content dynamically
 */
function resetSwiperInModal(dialogEl) {
    let targetPanel = queryFirst('.js-tab-panel.active', dialogEl);

    if (!targetPanel) {
        targetPanel = queryFirst('.js-single', dialogEl); // no-tabs case
    }

    if (!targetPanel) return;

    const slider = queryFirst('.js-swiper', targetPanel);
    const { swiper } = slider || {};

    if (swiper && swiper.activeIndex !== 0) {
        const $spinner = $(dialogEl).find('.image-container:visible').spinner();

        $spinner.start();
        swiper.slideTo(0, 0);
        $spinner.stop();
    }
}

/**
 * Scrolls active sheet-page to top
 * @param {Object} component - html sheet
 */
function scrollSheetTop($sheet) {
    const $active = $sheet.find('.sheet-page:visible').first();

    $active.find('.sheet-body-content').scrollTop(0);
}

/**
 * Resets active page to the first page and scrolls to top
 */
function resetModalSheet($modal) {
    const $sheet = $modal.find('.sheet');

    $modal.find('.sheet-page').removeClass(ACTIVE_CLASS);
    requestAnimationFrame(() => scrollSheetTop($sheet));
}

/**
 * Activates tab
 * - adds active class to the selected tab and corresponding panel, removes active class from previously active tab and panel
 * - resets color swatches to the first option if they exist in the tab panel
 * - scrolls to top of the sheet
 * @param {Object} $clickedTab - clicked tab element
 * @param {string} styliticsPlacement - optional stylitics placement value to send with tabActivated event for analytics, e.g. 'main' for main PDP widget. If not provided, styliticsPlacement will be an empty string.
 */
function activateTab($clickedTab, styliticsPlacement) {
    const targetPanelId = $clickedTab.attr('aria-controls');
    const $sheet = $clickedTab.closest('.sheet');
    const targetPanel = document.getElementById(targetPanelId);

    if (!hasClass(targetPanel, ACTIVE_CLASS)) {
        $sheet.find('.js-tab-controls button').removeClass(ACTIVE_CLASS).attr('aria-selected', 'false').attr('tabindex', '-1');

        $sheet.find('.js-tab-panel').removeClass(ACTIVE_CLASS);

        $clickedTab.addClass(ACTIVE_CLASS).attr('aria-selected', 'true').attr('tabindex', '0');

        addClass(targetPanel, ACTIVE_CLASS);
        requestAnimationFrame(() => {
            scrollSheetTop($sheet);
        });

        // update opener for next modal usage:
        const $updatedTrigger = $(`.js-stylitics-look-trigger[data-index="${$clickedTab.data('index')}"]`);
        if ($updatedTrigger.length) {
            $sheet.data('opener', $updatedTrigger);
        } else {
            const outfitEl = $sheet.data('styliticsOutfitEl');
            const itemIndex = $clickedTab.closest('.js-tab-controls').find('button').index($clickedTab);
            const item = outfitEl && outfitEl.querySelectorAll('.stylitics-grid-item')[itemIndex];

            if (item) {
                if (!item.hasAttribute('tabindex')) {
                    item.setAttribute('tabindex', '-1');
                }
                $sheet.data('opener', item);
            }
        }
    }

    // if tab has color swatches, reset to the first color:
    const $firstSwatch = $(targetPanel).find('.js-tile-swatch').first();
    if (!$firstSwatch.hasClass(SELECTED_CLASS)) {
        $firstSwatch.click();
    } else {
        const dialogEl = targetPanel.closest('.sheet-body');
        resetSwiperInModal(dialogEl);
    }

    // used by components to send analytics when tab is displayed:
    const tabEvent = new CustomEvent('tabActivated', {
        detail: { tabActive: targetPanel, styliticsPlacement: styliticsPlacement },
        bubbles: true,
        cancelable: false
    });

    targetPanel.dispatchEvent(tabEvent);

    requestAnimationFrame(() => {
        $clickedTab.focus();
    });
}

module.exports = {
    resetSwiperInModal,
    scrollSheetTop,
    activateTab,
    resetModalSheet
};
